import React, { useState, useMemo } from 'react';
import { View, StyleSheet, useWindowDimensions, Platform } from 'react-native';
import { useAppContext } from '../contexts/AppContext';
import { colors } from '../theme';
import { designTokens } from '../theme/design-system';
import { DashboardRail, DashboardRailItem } from './dashboard/DashboardRail';
import { ContactsSlideOver } from './dashboard/ContactsSlideOver';
import { DashboardContent } from './dashboard/DashboardContent';
import { DashboardToolbar } from './dashboard/DashboardToolbar';
import { useDashboardData } from './dashboard/useDashboardData';
import { MoreOptionsModal } from '../components/MoreOptionsModal';
import { useThemedStyles } from '../hooks/useThemedStyles';

/**
 * New admin analytics dashboard — a self-contained screen that does NOT use
 * ScreenLayout/Header/LeftPane/RightPane (the shell every other screen
 * shares). Instead: a vertical collapsible icon rail (replaces the top
 * header + settings-pane concepts) and a slide-over contacts panel (replaces
 * the always-on fixed RightPane), both scoped to this screen only.
 *
 * Reachable via navigateTo('dashboard') for now — the existing Home screen
 * is untouched; this only becomes 'home' once explicitly approved.
 *
 * Uses useWindowDimensions (reactive) rather than this codebase's dominant
 * Dimensions.get() snapshot pattern, since the rail/slide-over must reflow
 * live as the viewport changes — a deliberate, scoped deviation for this
 * screen only.
 */
export const DashboardScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();
  const { width } = useWindowDimensions();

  const isMobile = width < designTokens.breakpoints.tablet;
  const isTablet = width >= designTokens.breakpoints.tablet && width < designTokens.breakpoints.desktop;
  const isDesktop = width >= designTokens.breakpoints.desktop;

  const [userCollapsed, setUserCollapsed] = useState(false);
  // Mobile forces collapsed regardless of the user's own toggle state.
  const railCollapsed = isMobile ? true : userCollapsed;

  const [contactsOpen, setContactsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const panelWidth = useMemo(() => {
    if (isMobile) return width;
    if (isTablet) return Math.min(400, width * 0.9);
    return 380;
  }, [isMobile, isTablet, width]);

  const contentColumns = isDesktop ? 4 : isTablet ? 3 : 2;

  const { stats, flowSessions, assignmentStats, campaigns, users, templates, loading, error, refresh, dateRange, setDateRange } = useDashboardData();

  const railItems: DashboardRailItem[] = [
    { id: 'home', icon: 'home', label: 'Dashboard', active: true, onPress: () => {} },
    { id: 'contacts', icon: 'people-outline', label: 'Contacts', active: contactsOpen, onPress: () => setContactsOpen(true) },
    { id: 'calendar', icon: 'calendar-outline', label: 'Calendar', onPress: () => navigateTo('business-calendar') },
    { id: 'orders', icon: 'receipt-outline', label: 'Orders', onPress: () => navigateTo('orders') },
    { id: 'more', icon: 'ellipsis-horizontal', label: 'More', onPress: () => setMoreOpen(true) },
  ];

  const footerItems: DashboardRailItem[] = [
    { id: 'settings', icon: 'settings-outline', label: 'Settings', onPress: () => navigateTo('settings') },
  ];

  return (
    <View style={styles.root}>
      <DashboardRail
        items={railItems}
        footerItems={footerItems}
        collapsed={railCollapsed}
        showCollapseToggle={!isMobile}
        onToggleCollapse={() => setUserCollapsed((v) => !v)}
      />
      <View style={styles.content}>
        <DashboardToolbar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onRefresh={refresh}
          refreshing={loading}
        />
        <DashboardContent
          columns={contentColumns}
          loading={loading}
          error={error}
          stats={stats}
          flowSessions={flowSessions}
          assignmentStats={assignmentStats}
          campaigns={campaigns}
          users={users}
          templates={templates}
          onTeamViewAll={() => navigateTo('dashboard-team-all')}
          onTeamSettings={() => navigateTo('user-setup')}
          onTemplatesViewAll={() => navigateTo('dashboard-templates-all')}
          onTemplatesSettings={() => navigateTo('template-management')}
          onFlowsViewAll={() => navigateTo('dashboard-flows-all')}
          onAssignmentsViewAll={() => navigateTo('dashboard-assignments-all')}
        />
      </View>
      <ContactsSlideOver visible={contactsOpen} onClose={() => setContactsOpen(false)} panelWidth={panelWidth} />
      <MoreOptionsModal visible={moreOpen} onClose={() => setMoreOpen(false)} />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background.base,
    ...(Platform.OS === 'web' && { height: '100vh' as any }),
  },
  content: {
    flex: 1,
  },
});
